import { Suspense } from "react";

import {
  ESpinningLoaderSize,
  SpinningLoader,
} from "@/components/SpinningLoader";

import { RouteErrorBoundary } from "./RouteErrorBoundary";

interface IRouteWrapperProps {
  children: React.ReactNode;
}

/**
 * NOTE: NSAI-6259 (Alex) — "sometimes during the chat page loading there is an
 * empty page state but with an already loaded sidebar ... until load finishes",
 * and "we don't see anything for 2 secs".
 *
 * The fallback was not missing; it was invisible. It drew two skeleton blocks
 * that overrode `bg-skeleton-background` with `bg-background` — the page's own
 * colour — so a route chunk that took a moment painted a page-coloured
 * rectangle onto a page-coloured background and pulsed it.
 *
 * It is a centred spinner rather than a skeleton of the page. A skeleton has to
 * guess the shape of what is coming, and this one wrapper serves every lazy
 * route: a toolbar bar over a body panel is the chat and settings shape, and it
 * is wrong for the pages that do not have a toolbar, so on those it announced a
 * layout that never arrived. A spinner claims nothing about the page behind it,
 * which is the only honest thing to show when the module has not parsed yet and
 * this component cannot know what it renders.
 *
 * This is the last of three loading states, not a fourth: the boot splash
 * covers the app starting, a page's own query states cover its data, and this
 * covers the gap between a route being asked for and its chunk being parsed.
 */
export const RouteWrapper = ({ children }: IRouteWrapperProps) => {
  return (
    <RouteErrorBoundary>
      <Suspense
        fallback={
          <div
            className="flex h-full w-full items-center justify-center"
            data-testid="route-loading"
          >
            <SpinningLoader size={ESpinningLoaderSize.SMALL} />
          </div>
        }
      >
        {children}
      </Suspense>
    </RouteErrorBoundary>
  );
};
